import type { ColumnDef } from '@/shared/types/table-filters.types';
import type {
  EmploymentType,
  ExperienceLevel,
  JobPosition,
  JobPositionStatus,
  RemotePolicy,
} from '../types/jobPosition.types';

const EMPLOYMENT_TYPES: EmploymentType[] = [
  'FULL_TIME',
  'PART_TIME',
  'CONTRACT',
  'FREELANCE',
  'INTERNSHIP',
];

const EMPLOYMENT_TYPE_LABELS: Record<string, string> = {
  FULL_TIME: 'Full Time',
  PART_TIME: 'Part Time',
  CONTRACT: 'Contract',
  FREELANCE: 'Freelance',
  INTERNSHIP: 'Internship',
};

const REMOTE_POLICIES: RemotePolicy[] = ['REMOTE', 'HYBRID', 'ON_SITE'];

const REMOTE_POLICY_LABELS: Record<string, string> = {
  REMOTE: 'Remote',
  HYBRID: 'Hybrid',
  ON_SITE: 'On Site',
};

const EXPERIENCE_LEVELS: ExperienceLevel[] = [
  'ENTRY',
  'JUNIOR',
  'MID',
  'SENIOR',
  'LEAD',
  'EXECUTIVE',
];

const JOB_POSITION_STATUSES: JobPositionStatus[] = ['UPCOMING', 'OPEN', 'CLOSED', 'ARCHIVED'];

const JOB_POSITION_STATUS_LABELS: Record<string, string> = {
  UPCOMING: 'Upcoming',
  OPEN: 'Open',
  CLOSED: 'Closed',
  ARCHIVED: 'Archived',
};

/** Filterable/sortable job position fields, shared by the desktop table and the mobile cards. */
export const jobPositionTableColumns: ColumnDef<JobPosition>[] = [
  {
    key: 'title',
    label: 'Title',
    filterType: 'text',
    accessor: (jobPosition) => jobPosition.title,
    sortable: true,
  },
  {
    key: 'department',
    label: 'Department',
    filterType: 'text',
    accessor: (jobPosition) => jobPosition.department,
    sortable: true,
  },
  {
    key: 'location',
    label: 'Location',
    filterType: 'text',
    accessor: (jobPosition) => jobPosition.location,
    sortable: true,
  },
  {
    key: 'employmentType',
    label: 'Type',
    filterType: 'select',
    accessor: (jobPosition) => jobPosition.employmentType,
    options: EMPLOYMENT_TYPES,
    optionLabels: EMPLOYMENT_TYPE_LABELS,
    sortable: true,
  },
  {
    key: 'remotePolicy',
    label: 'Remote',
    filterType: 'select',
    accessor: (jobPosition) => jobPosition.remotePolicy,
    options: REMOTE_POLICIES,
    optionLabels: REMOTE_POLICY_LABELS,
    sortable: true,
  },
  {
    key: 'experienceLevels',
    label: 'Level',
    filterType: 'multi-select',
    accessor: (jobPosition) => jobPosition.experienceLevels,
    options: EXPERIENCE_LEVELS,
    // Sort by the most senior level requested (ordinal rank), not by enum string order.
    sortValue: (jobPosition) => {
      const ranks = (jobPosition.experienceLevels ?? [])
        .map((level) => EXPERIENCE_LEVELS.indexOf(level))
        .filter((rank) => rank >= 0);
      return ranks.length ? Math.max(...ranks) : undefined;
    },
    sortable: true,
  },
  {
    key: 'status',
    label: 'Status',
    filterType: 'select',
    accessor: (jobPosition) => jobPosition.status,
    options: JOB_POSITION_STATUSES,
    optionLabels: JOB_POSITION_STATUS_LABELS,
    sortable: true,
  },
  {
    key: 'createdAt',
    label: 'Created',
    filterType: 'date-range',
    accessor: (jobPosition) => jobPosition.createdAt,
    sortable: true,
  },
];
